import { FormRichTextEditor } from '@exam-management/rich-text-editor';
import type { CommentItem } from '@exam-management/task-management';
import {
  Box,
  Button,
  CommentOutlinedIcon,
  DescriptionOutlinedIcon,
  Flex,
  InputAdornment,
  Loading,
  ReadonlyField,
  Text,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';
import { useFormContext } from 'react-hook-form';

import { CommentRichTextPreviewer } from '../../components';
import { useUploadImageForQsetReview } from '../../hooks';

export function EmptyCommentSection() {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        m: 'auto',
        gap: (theme) => theme.spacing(4),
      }}
    >
      <CommentOutlinedIcon color='secondary' sx={{ fontSize: 100 }} />
      <Text variant='h3'>ยังไม่มีความเห็นในส่วนนี้</Text>
    </Flex>
  );
}

export function ProhibitedCommentSection() {
  return (
    <Flex
      sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', m: 'auto' }}
    >
      <DescriptionOutlinedIcon
        color='secondary'
        sx={(theme) => ({ mb: theme.spacing(4), fontSize: 100 })}
      />
      <Text variant='h3' sx={(theme) => ({ mb: theme.spacing(2), fontWeight: 'semibold' })}>
        ยังไม่สามารถดูเนื้อหาส่วนนี้ได้
      </Text>
      <Text>รอให้ผู้ทบทวนชุดข้อสอบเสร็จแล้วจึงจะสามารถดูเนื้อหาได้</Text>
    </Flex>
  );
}

interface CommentProps {
  author: string;
  authorEmail: string;
  message: string;
  commentedAt: Date | string;
  image?: string;
  comment: CommentItem;
}

export function Comment(props: Readonly<CommentProps>) {
  const { author, message, commentedAt, comment } = props;

  return (
    <ReadonlyField
      content={
        <Flex sx={{ gap: (theme) => theme.spacing(3), flexDirection: 'column' }}>
          <Flex sx={{ gap: (theme) => theme.spacing(2), alignItems: 'center' }}>
            <Text variant='body1Semibold'>{author}</Text>
            <Text variant='body2' color='textSecondary'>
              {formatDateTime(new Date(commentedAt))}
            </Text>
          </Flex>
          <CommentRichTextPreviewer content={message} taskId={comment.taskId} />
        </Flex>
      }
    />
  );
}

interface CommentAreaProps {
  isReadonly?: boolean;
  comments: CommentItem[];
  taskId: string;
}

export function CommentArea(props: Readonly<CommentAreaProps>) {
  const { isReadonly = false, comments, taskId } = props;
  const { formState } = useFormContext();
  const { handleUpload, loading: isLoading } = useUploadImageForQsetReview({
    taskId,
  });

  return (
    <>
      {!isReadonly ? (
        <Flex sx={{ flexDirection: 'column' }}>
          <Box
            sx={(theme) => ({
              '.MuiBox-root': {
                borderRadius: theme.shape.rounded(2, 2, 0, 0),
                '*:focus-within': {
                  boxShadow: 'none',
                  borderColor: theme.palette.grey[300],
                },
              },
            })}
          >
            <FormRichTextEditor
              name='comment'
              onImageUpload={handleUpload}
              loading={isLoading}
              placeholder='Add a Comment...'
              rows={2}
            />
          </Box>
          <Flex
            sx={(theme) => ({
              border: `1px solid ${theme.palette.grey[300]}`,
              borderTop: 'none',
              borderRadius: theme.shape.rounded(0, 0, 2, 2),
              p: theme.spacing(3),
              minHeight: 60,
              justifyContent: 'flex-end',
            })}
          >
            <InputAdornment position='end'>
              <Button
                size='small'
                type='submit'
                sx={{ minWidth: 44, maxHeight: 32 }}
                loading={formState.isSubmitting}
              >
                Add Comment
              </Button>
            </InputAdornment>
          </Flex>
        </Flex>
      ) : null}
      <Flex
        sx={(theme) => ({
          height: 1,
          overflowY: 'auto',
          flexDirection: 'column',
          gap: theme.spacing(6),
          py: theme.spacing(4),
        })}
      >
        {comments.length > 0
          ? comments.map((comment, index) => (
              <Comment
                key={`${comment.user.firstname}-${comment.user.lastname}-comment-${index}`}
                author={`${comment.user.firstname} ${comment.user.lastname}`}
                authorEmail={comment.user.email}
                image={comment.user.image ?? ''}
                message={comment.content}
                commentedAt={comment.createdAt}
                comment={comment}
              />
            ))
          : null}
      </Flex>
    </>
  );
}

interface CommentFormProps {
  comments: CommentItem[];
  variant?: 'HIDDEN' | 'EMPTY' | 'PROHIBITED' | 'EDITABLE' | 'READONLY' | null;
  loading?: boolean;
  taskId: string;
}

export default function CommentForm(props: Readonly<CommentFormProps>) {
  const { comments, variant = 'EMPTY', loading: isLoading = false, taskId } = props;

  const renderCommentForm = () => {
    if (isLoading) {
      return <Loading sx={{ m: 'auto' }} />;
    } else {
      switch (variant) {
        case 'EMPTY':
          return <EmptyCommentSection />;
        case 'PROHIBITED':
          return <ProhibitedCommentSection />;
        case 'EDITABLE':
          return <CommentArea comments={comments} taskId={taskId} />;
        case 'READONLY':
          return <CommentArea isReadonly comments={comments} taskId={taskId} />;
        case 'HIDDEN':
        default:
          return null;
      }
    }
  };

  return (
    <Flex
      sx={(theme) => ({
        height: 'fit-content',
        flexDirection: 'column',
        gap: theme.spacing(2),
        flex: 1,
      })}
    >
      {renderCommentForm()}
    </Flex>
  );
}
